import type { SessionBody } from '@/shared/api/generated/model';
import { appConfig } from '@/shared/config';

import { clearSession, maySignedIn, setSession } from './session';

/**
 * Обновление доступа по cookie.
 *
 * Идёт голым `fetch`, а не через общий `apiFetch`, намеренно: тот при
 * ответе 401 сам зовёт обновление, и обновление через него зациклилось бы на
 * первом же протухшем токене.
 */

let inFlight: Promise<boolean> | null = null;

/** Имя замка, за которым вкладки одного браузера обновляются по очереди. */
const LOCK_NAME = 'flowdo.refresh';

/**
 * Обновляет сеанс. Одновременные вызовы делят один запрос.
 *
 * Единый поток здесь обязателен: после протухания токена все запросы экрана
 * получают 401 разом. Без него десяток запросов начал бы десять обновлений,
 * а поскольку токен обновления одноразовый, девять из них были бы приняты за
 * попытку кражи — и человека выкинуло бы из системы за то, что он открыл
 * страницу с несколькими списками.
 *
 * То же самое между вкладками: cookie у них одна, а память — у каждой своя.
 * Браузер, восстановивший две вкладки после запуска, обновлял бы обе разом,
 * и вторая приходила бы на сервер с токеном, который первая уже сменила.
 * Поэтому запрос идёт под замком `navigator.locks` — вкладки встают в
 * очередь и каждая обновляется уже со свежей cookie. Сервер к тому же
 * прощает повтор только что сменённого токена, но лучше до него не доводить.
 */
export function refreshSession(): Promise<boolean> {
  inFlight ??= locked(run).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

function locked(task: () => Promise<boolean>): Promise<boolean> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;

  if (locks === undefined) {
    return task();
  }

  return locks.request(LOCK_NAME, task);
}

async function run(): Promise<boolean> {
  // Тому, кто вышел или получил отказ, спрашивать нечего: ответ известен,
  // а 401 в консоли выглядит как ошибка.
  if (!maySignedIn()) {
    clearSession();

    return false;
  }

  const base = appConfig.apiBaseUrl.replace(/\/$/, '');

  try {
    const response = await fetch(`${base}/auth/refresh`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      clearSession();

      return false;
    }

    const body = (await response.json()) as SessionBody;

    setSession(body.access_token, body.user);

    return true;
  } catch {
    // Сеть отвалилась. Сеанс не трогаем: обрыв связи — не повод считать
    // человека вышедшим, он вернётся на ту же страницу.
    return false;
  }
}
